import React from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';

import Grid from '~/components/core/Atomic/Grid/Grid';
import DialogFooterActions from '~/components/core/DialogFooterActions';

import { cleanEmptyValues, nullValuesToEmptyStrings } from '../../../../../Utils';
import CardDialog from '../../../../CardDialog';
import { buildSectionSchema, dropUnfilledSeededValues, seedSectionValues } from '../sectionCompletion';

const AdditionalInformationDialog = ({
  id,
  claimId,
  initialValues,
  onCancel,
  onSubmit,
  open,
  title,
  showOnly,
  additionalFieldOmitFn,
  AdditionalInformationFragment,
  AdditionalInformationFragmentInitialValues,
  AdditionalInformationFragmentValidationSchema,
}) => {
  if (!open) {
    return <></>;
  }

  // What the dialog opened with before it started seeding: the section's own stored values when it has
  // any, and the fragment's seed otherwise. The save path below measures the seed against this, so the
  // payload keeps exactly the keys it carried before — the seed renders and validates, it does not write.
  const priorValues = initialValues || AdditionalInformationFragmentInitialValues;

  return (
    <>
      <Formik
        // Seeded over the fragment's initial values, the same way the banner's gate validates them: a field
        // a sub-type change has just brought into scope has no stored key, and the two must judge it alike
        // or a save the dialog accepts leaves the gate closed with no error shown on the field.
        initialValues={seedSectionValues({
          seedValues: AdditionalInformationFragmentInitialValues,
          sectionValues: initialValues && nullValuesToEmptyStrings(initialValues),
        })}
        validationSchema={buildSectionSchema(AdditionalInformationFragmentValidationSchema)}
        onSubmit={async (values, { setSubmitting }) => {
          try {
            // cleanEmptyValues is shallow, so it cannot see a blank the seed added inside a nested object
            // (a section's configured_fields_values). The claim-side callers PATCH what comes out of here
            // straight onto a live incident, and the backend merges per key, so a seeded blank that got
            // this far would write an answer the incident never held.
            await onSubmit(cleanEmptyValues(dropUnfilledSeededValues({ values, priorValues })));
            setSubmitting(false);
          } catch (error) {
            setSubmitting(false);
          }
        }}
      >
        {({ isSubmitting, handleSubmit }) => (
          <CardDialog
            isDialog={true}
            title={title}
            maxWidth="sm"
            fullWidth
            onClose={onCancel}
            preventClose={isSubmitting}
            footerActions={
              !showOnly && (
                <DialogFooterActions
                  disabled={isSubmitting}
                  onClickPrimary={handleSubmit}
                  onClickSecondary={onCancel}
                />
              )
            }
          >
            <Grid container spacing={2}>
              {AdditionalInformationFragment && (
                <AdditionalInformationFragment
                  additionalFieldOmitFn={additionalFieldOmitFn}
                  sectionId={id}
                  claimId={claimId}
                />
              )}
            </Grid>
          </CardDialog>
        )}
      </Formik>
    </>
  );
};

AdditionalInformationDialog.propTypes = {
  claimId: PropTypes.number,
  dialogAction: PropTypes.node,
  disableEditIdentity: PropTypes.bool,
  initialValues: PropTypes.object,
  isInsured: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  showOnly: PropTypes.bool,
  title: PropTypes.string.isRequired,
  AdditionalInformationFragment: PropTypes.node,
  AdditionalInformationFragmentInitialValues: PropTypes.object,
  AdditionalInformationFragmentValidationSchema: PropTypes.object,
  additionalFieldOmitFn: PropTypes.func,
  id: PropTypes.string,
};

export default AdditionalInformationDialog;
